import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import AutocompleteTaxRateSchedulesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteTaxRateSchedulesTypeahead';
import { TextFormField } from '~components/FormFields/TextFormField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';

import {
  type OrderFormV3FieldValues,
  useOrderFormV3Context,
} from '../OrderFormV3Context';

/**
 * Renders the tax card. Render-only: the show/hide PseudoLink over a
 * Collapse holding the five tax fields — the schedule typeahead (scoped to
 * the order's selected company), the manual rate disabled while a schedule
 * is selected, and the three taxable-scope checkboxes. The section visibility, its toggle and the
 * schedule watch all arrive via context; the schedule→rate auto-populate
 * lives in effects/useTaxRateAutopopulate.
 */
export function TaxPanel(): JSX.Element {
  const {
    isTaxRequired,
    isTaxSectionVisible,
    toggleTaxSectionVisibility,
    watchedCompany,
    watchedTaxRateSchedule,
  } = useOrderFormV3Context();
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<OrderFormV3FieldValues>();
  const taxableScopeError = errors.taxFreight?.message;
  // The scope error lives on taxFreight, so changing a sibling field must re-check it.
  const revalidateTaxableScope = () => {
    if (taxableScopeError) {
      void trigger('taxFreight');
    }
  };

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <PseudoLink
        action={toggleTaxSectionVisibility}
        title={
          isTaxSectionVisible
            ? t('order.form.hide_tax_rates')
            : t('order.form.show_tax_rates')
        }
        sx={{
          display: 'inline-block',
          ...(isTaxSectionVisible ? { mb: 2 } : {}),
        }}
      />

      <Collapse in={isTaxSectionVisible}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }} data-test-id="order-form-v3-tax-rate-schedule">
            <AutocompleteTaxRateSchedulesTypeahead
              key={watchedCompany?.id}
              control={control}
              name="taxRateSchedule"
              label={t('order.form.tax_rate_schedule')}
              isRequired={isTaxRequired}
              companyId={watchedCompany?.id || ''}
              onValueChange={revalidateTaxableScope}
            />
          </Grid>
          <Grid size={{ xs: 3 }} data-test-id="order-form-v3-tax-rate">
            <TextFormField
              control={control}
              disabled={Boolean(watchedTaxRateSchedule)}
              errors={errors}
              label={t('order.form.tax_rate')}
              name="taxRate"
              type="number"
              inputProps={{ step: '0.0001' }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2.5 }}>
          <Grid size={{ xs: 4 }} data-test-id="order-form-v3-tax-freight">
            <CheckboxInput
              control={control}
              label={t('order.form.taxable_freight')}
              name="taxFreight"
              onValueChange={revalidateTaxableScope}
            />
          </Grid>
          <Grid size={{ xs: 4 }} data-test-id="order-form-v3-tax-material">
            <CheckboxInput
              control={control}
              label={t('order.form.taxable_material')}
              name="taxMaterial"
              onValueChange={revalidateTaxableScope}
            />
          </Grid>
          <Grid size={{ xs: 4 }} data-test-id="order-form-v3-tax-add-ons">
            <CheckboxInput
              control={control}
              label={t('order.form.taxable_add_ons')}
              name="taxAddOns"
              onValueChange={revalidateTaxableScope}
            />
          </Grid>
        </Grid>

        {taxableScopeError && (
          <FormHelperText error sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
            {taxableScopeError}
          </FormHelperText>
        )}
      </Collapse>
    </Card>
  );
}
